<template>
  <div class="folder-archive-panel">
    <button
      v-if="!isPreparing && !isDone"
      type="button"
      class="btn btn-outline-secondary btn-sm w-100 mb-2"
      :disabled="loading"
      @click="onDownloadClick"
    >
      <i class="fas fa-download mr-1"></i>{{ createLabel }}
    </button>
    <span v-else-if="isPreparing" class="btn btn-outline-secondary btn-sm w-100 mb-2 disabled" aria-disabled="true">
      <i v-if="justExecuted" class="fas fa-spinner fa-spin mr-1"></i>{{ creatingLabel }}
    </span>

    <div v-if="isPreparing && pollTimeoutReached" class="small text-muted mb-2">
      {{ preparingTimeoutText }}
    </div>

    <div v-if="isFailed && archive && archive.error_message" class="text-danger small mb-2">
      {{ archive.error_message }}
    </div>
    <div v-if="errorMessage" class="text-danger small mb-2">
      {{ errorMessage }}
    </div>

    <fast-dl
      v-if="isDone && archive"
      class="mb-2 w-100"
      :video-id="archive.uuid"
      :signed-url-endpoint="downloadUrlsEndpoint"
      :known-total-bytes="Number(archive.total_size || 0)"
      range-supported-hint="bytes"
      :chunk-mb="64"
      :initial-concurrency="12"
      :max-concurrency="20"
      :ordered-write="true"
      :start-label="downloadLabel"
    >
      <template #default>
        <i class="fas fa-download mr-1"></i>{{ downloadLabel }}
      </template>
    </fast-dl>
    <div v-if="isDone && archive && archive.created_at" class="small text-muted mb-2">
      {{ createdAtText }}
    </div>
    <button
      v-if="isDone"
      type="button"
      class="btn btn-outline-secondary btn-sm w-100 mb-2"
      :disabled="loading"
      @click="onDownloadClick"
    >
      {{ recreateLabel }}
    </button>

    <div v-if="skippedCount > 0" class="small text-muted mb-2 folder-archive-skipped">
      <details>
        <summary>{{ skippedSummaryText }}</summary>
        <ul class="mb-0 pl-3">
          <li v-for="(item, idx) in skippedItems" :key="idx">
            {{ item.name }}<template v-if="item.reason"> - {{ item.reason }}</template>
          </li>
        </ul>
        <div v-if="skippedTruncated">{{ skippedTruncatedText }}</div>
      </details>
    </div>

    <!-- クラウドストレージ階層転送 -->
    <div class="folder-cloud-export-buttons">
      <template v-for="provider in cloudProviderList" :key="provider">
        <button
          v-if="!cloudIsRunning(provider)"
          type="button"
          class="btn btn-outline-secondary btn-sm w-100 mb-2"
          :disabled="cloudIsLoading(provider)"
          @click="onCloudExportClick(provider)"
        >
          <i :class="cloudProviderIcon(provider)" class="mr-1"></i>{{ cloudIsLoading(provider) ? cloudPreparingLabel : cloudProviderLabel(provider) }}
        </button>
        <span v-else class="btn btn-outline-secondary btn-sm w-100 mb-2 disabled" aria-disabled="true">
          {{ cloudProviderInProgressLabel(provider) }}
        </span>
        <div v-if="cloudIsRunning(provider) && cloudSkippedCount(provider) > 0" class="small text-muted mb-2 folder-archive-skipped">
          <details>
            <summary>{{ cloudSkippedSummaryText(provider) }}</summary>
            <ul class="mb-0 pl-3">
              <li v-for="(item, idx) in cloudSkippedItems(provider)" :key="idx">
                {{ item.name }}<template v-if="item.reason"> - {{ item.reason }}</template>
              </li>
            </ul>
            <div v-if="cloudSkippedTruncated(provider)">{{ cloudSkippedTruncatedText(provider) }}</div>
          </details>
        </div>
        <div v-if="cloudFailedCount(provider) > 0" class="text-danger small mb-2 folder-archive-failed">
          <details>
            <summary>{{ cloudFailedSummaryText(provider) }}</summary>
            <ul class="mb-0 pl-3">
              <li v-for="(item, idx) in cloudFailedItems(provider)" :key="idx">
                {{ item.path }}<template v-if="item.reason"> - {{ item.reason }}</template>
              </li>
            </ul>
            <div v-if="cloudFailedTruncated(provider)">{{ cloudFailedTruncatedText(provider) }}</div>
          </details>
        </div>
        <div v-if="cloudErrorMessage(provider)" class="text-danger small mb-2">
          {{ cloudErrorMessage(provider) }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, watch } from 'vue';
import _ from 'lodash';
import { useFolderArchive } from '../dashboard/useFolderArchive';
import { useFolderCloudExport } from '../dashboard/useFolderCloudExport';

const props = defineProps({
  folderId: { type: [Number, String, null], default: null },
  enabledProviders: {
    type: Object,
    default: () => ({}),
  },
});

const {
  archive,
  loading,
  errorMessage,
  isPreparing,
  isFailed,
  isDone,
  skippedCount,
  skippedItems,
  skippedTruncated,
  pollTimeoutReached,
  justExecuted,
  fetchLatest,
  execute,
  stopPolling,
} = useFolderArchive();

const {
  isLoading: cloudIsLoading,
  isRunning: cloudIsRunning,
  skippedCount: cloudSkippedCount,
  skippedItems: cloudSkippedItems,
  skippedTruncated: cloudSkippedTruncated,
  failedCount: cloudFailedCount,
  failedItems: cloudFailedItems,
  failedTruncated: cloudFailedTruncated,
  errorMessage: cloudErrorMessage,
  fetchLatest: fetchCloudLatest,
  execute: executeCloudExport,
} = useFolderCloudExport();

const cloudSkippedSummaryText = (provider) =>
  translate('folder.download_skipped', ':count件のファイルは対象外のため含まれません。').replace(
    ':count',
    String(cloudSkippedCount(provider))
  );
const cloudSkippedTruncatedText = (provider) =>
  translate('folder.download_skipped_truncated', '他 :count 件').replace(
    ':count',
    String(cloudSkippedCount(provider) - cloudSkippedItems(provider).length)
  );
const cloudFailedSummaryText = (provider) =>
  translate('folder.cloud_export_failed_summary', ':count件のファイルの転送に失敗しました。').replace(
    ':count',
    String(cloudFailedCount(provider))
  );
const cloudFailedTruncatedText = (provider) =>
  translate('folder.cloud_export_failed_truncated', '他 :count 件').replace(
    ':count',
    String(cloudFailedCount(provider) - cloudFailedItems(provider).length)
  );

const CLOUD_PROVIDER_ICONS = {
  onedrive: 'fab fa-microsoft',
  sharepoint: 'fab fa-microsoft',
  googledrive: 'fab fa-google-drive',
  dropbox: 'fab fa-dropbox',
  box: 'fas fa-box',
};

const cloudProviderList = computed(() =>
  ['onedrive', 'sharepoint', 'googledrive', 'dropbox', 'box'].filter(
    (provider) => props.enabledProviders && props.enabledProviders[provider]
  )
);

const PROVIDER_TRANSFER_KEY = {
  onedrive: 'OneDrive transfer',
  sharepoint: 'SharePoint transfer',
  googledrive: 'Google Drive transfer',
  dropbox: 'Dropbox transfer',
  box: 'Box transfer',
};
const providerTransferKey = (provider) => PROVIDER_TRANSFER_KEY[provider] || provider;

const cloudProviderIcon = (provider) => CLOUD_PROVIDER_ICONS[provider] || 'fas fa-cloud-upload-alt';
const cloudProviderLabel = (provider) => translate(providerTransferKey(provider), provider);
const cloudProviderInProgressLabel = (provider) =>
  translate(`${providerTransferKey(provider)} in progress`, provider);

const onCloudExportClick = (provider) => executeCloudExport(props.folderId, provider);

const translate = (key, fallback = '') =>
  _.get(window.i18n, key) || fallback || key;

const downloadLabel = computed(() => translate('button.folder_archive_download', 'ZIPをダウンロード'));
const downloadReadyLabel = computed(() => translate('button.folder_download_ready', 'フォルダダウンロード準備中'));
const createLabel = computed(() => translate('button.folder_archive_create', 'ZIPを作成'));
const creatingLabel = computed(() => translate('button.folder_archive_creating', 'ZIP準備中'));
const recreateLabel = computed(() => translate('button.folder_archive_recreate', 'ZIPを作り直す'));
const cloudPreparingLabel = computed(() => translate('button.cloud_export_preparing', '転送準備中'));
const preparingTimeoutText = computed(() =>
  translate('folder.download_preparing_timeout', '時間がかかっています。準備ができるとメールが届きます。')
);
const skippedSummaryText = computed(() =>
  translate('folder.download_skipped', ':count件のファイルは対象外のため含まれません。').replace(
    ':count',
    String(skippedCount.value)
  )
);
// created_at はサーバー(Asia/Tokyo)のオフセット付きISO8601 (例 2026-08-31T11:24:19+09:00)。
// dayjs等で整形すると閲覧者のブラウザのタイムゾーンに変換されてしまうため、
// 履歴画面(サーバー側でformat)と同じサーバータイムゾーンの表示に揃える目的で文字列から直接取り出す。
const createdAtText = computed(() => {
  if (!archive.value || !archive.value.created_at) {
    return '';
  }
  return translate('folder.download_created_at', '作成日時: :datetime')
    .replace(':datetime', String(archive.value.created_at).slice(0, 16).replace('T', ' '));
});
const skippedTruncatedText = computed(() => `他 ${skippedCount.value - skippedItems.value.length} 件`);

const downloadUrlsEndpoint = computed(() => {
  if (!archive.value || !archive.value.id) {
    return '';
  }
  return `/api/folders/archives/${archive.value.id}/download-urls`;
});

const onDownloadClick = () => execute(props.folderId);

onMounted(() => {
  fetchLatest(props.folderId);
  fetchCloudLatest(props.folderId);
});
watch(
  () => props.folderId,
  (next) => {
    stopPolling();
    fetchLatest(next);
    fetchCloudLatest(next);
  }
);
onBeforeUnmount(() => {
  stopPolling();
});
</script>
